// The player's state for the launch answer kinds, shared by the board (fill blanks, ordered taps, Yes/No
// rows beside a grid) and the answer area (keypad, digit slots, counters). PuzzleView owns it through
// `useLaunchPlay`; the host owns the selection (controlled, like choice and cellSet).
import { useState } from "react";
import type { Answer, AnswerSpec, CellRef, FillAnswerSpec, Glyph, NumberAnswerSpec, Selection, SequenceAnswerSpec } from "../types";
import {
  cellKey,
  cellList,
  digitsLength,
  digitsText,
  fillKeys,
  fillRange,
  fillValues,
  isLaunchAnswerSpec,
  numberMaxDigits,
  numberText,
  rowsCount,
  rowsValue,
  sequenceCells,
  sequenceCount,
  type LaunchAnswerKind,
  type LaunchAnswerSpec,
} from "./answers";

export interface FillState {
  cells: CellRef[];
  /** "r,c" → index into `cells`. */
  index: Map<string, number>;
  values: (number | null)[];
  /** The correct values, when revealed. */
  shown: (number | null)[] | null;
  keys: number[];
  multiDigit: boolean;
  negative: boolean;
  min: number;
  max: number;
}

export interface SequenceState {
  count: number;
  /** "r,c" keys of the tappable cells, or null for every cell. */
  tappable: Set<string> | null;
  taps: CellRef[];
  /** The correct order, when revealed. */
  shown: CellRef[] | null;
  stopOnError: boolean;
}

export interface LaunchPlay {
  kind: LaunchAnswerKind;
  spec: LaunchAnswerSpec;
  /** Play mode: inputs are live controls. Off in previews (drawn, not focusable). */
  interactive: boolean;
  /** Input frozen (disabled by the host, or revealed). */
  locked: boolean;
  /** The correct answer is shown. */
  revealed: boolean;
  selection: Selection | null;
  shown: Answer | null;
  /** Number and digits: the typed text. */
  text: string;
  maxDigits: number;
  fill: FillState | null;
  sequence: SequenceState | null;
  rows: { count: number; value: string; shown: string | null } | null;
  /** Fill: the blank typing goes into (index into `fill.cells`), or -1. */
  active: number;
  setActive: (i: number) => void;
  /** Types a digit (or "-") into the field, the slots, or a fill blank (`at`, default the active one). */
  type: (key: number | "-", at?: number) => void;
  erase: (at?: number) => void;
  clear: () => void;
  tap: (cell: CellRef) => void;
  undo: () => void;
  setRow: (row: number, value: "Y" | "N") => void;
  /** Number reveal: the value, drawn in the board's `slot` (N1's "?"). */
  slotFill: Glyph | null;
}

interface Options {
  answerSpec: AnswerSpec | undefined;
  selection: Selection | null | undefined;
  shown: Answer | null | undefined;
  onSelectionChange?: (selection: Selection | null) => void;
  interactive: boolean;
  locked: boolean;
}

/**
 * State for a launch answer kind, or null for choice / cellSet (and malformed specs). Call unconditionally
 * (it holds the active fill blank); everything else lives in the host's selection.
 */
export function useLaunchPlay({ answerSpec, selection: rawSelection, shown: rawShown, onSelectionChange, interactive, locked }: Options): LaunchPlay | null {
  const [focus, setFocus] = useState<{ active: number; fresh: boolean }>({ active: 0, fresh: true });
  if (!isLaunchAnswerSpec(answerSpec)) return null;
  const spec = answerSpec;
  const kind = spec.kind;
  const selection = rawSelection?.kind === kind ? rawSelection : null;
  const shown = rawShown?.kind === kind ? rawShown : null;
  const live = interactive && !locked && Boolean(onSelectionChange);
  const emit = (a: Selection | null) => {
    if (live) onSelectionChange?.(a);
  };

  let text = "";
  let maxDigits = 0;
  let fill: FillState | null = null;
  let sequence: SequenceState | null = null;
  let rows: LaunchPlay["rows"] = null;

  if (kind === "number") {
    text = numberText(selection);
    maxDigits = numberMaxDigits(spec);
  } else if (kind === "digits") {
    maxDigits = digitsLength(spec);
    text = digitsText(selection).slice(0, maxDigits);
  } else if (kind === "fill") {
    const cells = cellList((spec as FillAnswerSpec).cells);
    const { keys, multiDigit, negative, maxDigits: md } = fillKeys(spec as FillAnswerSpec);
    maxDigits = md;
    fill = {
      cells,
      index: new Map(cells.map((c, i) => [cellKey(c[0], c[1]), i])),
      values: fillValues(selection, cells.length),
      shown: shown ? fillValues(shown, cells.length) : null,
      keys,
      multiDigit,
      negative,
      ...fillRange(spec as FillAnswerSpec),
    };
  } else if (kind === "sequence") {
    const s = spec as SequenceAnswerSpec;
    const count = sequenceCount(s);
    const listed = Array.isArray(s.cells) ? cellList(s.cells) : null;
    sequence = {
      count,
      tappable: listed && listed.length ? new Set(listed.map((c) => cellKey(c[0], c[1]))) : null,
      taps: sequenceCells(selection).slice(0, count),
      shown: shown ? sequenceCells(shown) : null,
      stopOnError: Boolean(s.stopOnError),
    };
  } else if (kind === "rows") {
    const count = rowsCount(spec);
    rows = { count, value: rowsValue(selection, count), shown: shown ? rowsValue(shown, count) : null };
  }

  const active = fill ? Math.min(Math.max(0, focus.active), Math.max(0, fill.cells.length - 1)) : -1;

  const setActive = (i: number) => {
    if (!fill || i < 0 || i >= fill.cells.length) return;
    setFocus({ active: i, fresh: true });
  };

  const typeNumber = (key: number | "-") => {
    let next = text;
    if (key === "-") {
      if (!(spec as NumberAnswerSpec).allowNegative) return;
      next = next.startsWith("-") ? next.slice(1) : `-${next}`;
    } else {
      const digits = next.replace(/^-/, "");
      if (digits.length >= maxDigits && digits !== "0") return;
      const sign = next.startsWith("-") ? "-" : "";
      next = `${sign}${digits === "0" ? "" : digits}${key}`;
    }
    emit(next ? { kind: "number", value: next } : null);
  };

  const typeFill = (key: number | "-", at?: number) => {
    if (!fill) return;
    const i = at !== undefined && at >= 0 && at < fill.cells.length ? at : active;
    const fresh = i !== active || focus.fresh;
    const values = [...fill.values];
    const current = values[i];
    if (key === "-") {
      if (!fill.negative || current === null || current === undefined) return;
      values[i] = -current;
    } else if (!fill.multiDigit) {
      if (!fill.keys.includes(key)) return;
      values[i] = key;
    } else {
      const prev = fresh || current === null || current === undefined ? "" : String(Math.abs(current));
      let digits = prev.length >= maxDigits || prev === "0" ? String(key) : `${prev}${key}`;
      if (Number(digits) > Math.max(Math.abs(fill.max), Math.abs(fill.min))) digits = String(key);
      const sign = !fresh && current !== null && current !== undefined && current < 0 ? -1 : 1;
      values[i] = sign * Number(digits);
    }
    emit({ kind: "fill", value: values });
    // Single-digit blanks (KenKen, Kakuro) move on to the next empty blank; multi-digit ones once full.
    const full = !fill.multiDigit || String(Math.abs(values[i] ?? 0)).length >= maxDigits;
    if (full && key !== "-") {
      const n = values.length;
      const next = Array.from({ length: n - 1 }, (_, k) => (i + 1 + k) % n).find((j) => values[j] === null);
      setFocus({ active: next ?? i, fresh: next !== undefined || fill.multiDigit });
    } else setFocus({ active: i, fresh: false });
  };

  const type = (key: number | "-", at?: number) => {
    if (!live) return;
    if (kind === "number") typeNumber(key);
    else if (kind === "digits") {
      if (key === "-" || text.length >= maxDigits) return;
      emit({ kind: "digits", value: `${text}${key}` });
    } else if (kind === "fill") typeFill(key, at);
  };

  const erase = (at?: number) => {
    if (!live) return;
    if (kind === "number" || kind === "digits") {
      const next = text.slice(0, -1);
      emit(next && next !== "-" ? ({ kind, value: next } as Answer) : null);
    } else if (fill) {
      const i = at !== undefined && at >= 0 && at < fill.cells.length ? at : active;
      const fresh = i !== active || focus.fresh;
      const values = [...fill.values];
      const current = values[i];
      if (current === null || current === undefined) {
        // Nothing here: step back to the previous filled blank, as a keypad's ⌫ would.
        const prev = Array.from({ length: values.length }, (_, k) => (i - 1 - k + values.length) % values.length).find((j) => values[j] !== null);
        if (prev !== undefined) setFocus({ active: prev, fresh: false });
        return;
      }
      const digits = String(Math.abs(current));
      values[i] = fill.multiDigit && !fresh && digits.length > 1 ? Math.sign(current) * Number(digits.slice(0, -1)) : null;
      emit(values.some((v) => v !== null) ? { kind: "fill", value: values } : null);
      setFocus({ active: i, fresh: false });
    } else if (kind === "sequence") undo();
  };

  const clear = () => {
    if (!live) return;
    emit(null);
    setFocus({ active: 0, fresh: true });
  };

  const tap = (cell: CellRef) => {
    if (!live || !sequence) return;
    const key = cellKey(cell[0], cell[1]);
    if (sequence.tappable && !sequence.tappable.has(key)) return;
    const at = sequence.taps.findIndex((c) => cellKey(c[0], c[1]) === key);
    if (at >= 0) {
      // Tapping the latest tap again takes it back (never after a stopOnError round: taps are final).
      if (at === sequence.taps.length - 1 && !sequence.stopOnError) undo();
      return;
    }
    if (sequence.taps.length >= sequence.count) return;
    emit({ kind: "sequence", value: [...sequence.taps, [cell[0], cell[1]]] });
  };

  const undo = () => {
    if (!live || !sequence || sequence.stopOnError || sequence.taps.length === 0) return;
    const next = sequence.taps.slice(0, -1);
    emit(next.length ? { kind: "sequence", value: next } : null);
  };

  const setRow = (row: number, value: "Y" | "N") => {
    if (!live || !rows || row < 0 || row >= rows.count) return;
    const chars = [...rows.value];
    chars[row] = value;
    emit({ kind: "rows", value: chars.join("") });
  };

  const shownNumber = kind === "number" && shown ? numberText(shown) : "";
  return {
    kind,
    spec,
    interactive,
    locked,
    revealed: Boolean(shown),
    selection,
    shown,
    text,
    maxDigits,
    fill,
    sequence,
    rows,
    active,
    setActive,
    type,
    erase,
    clear,
    tap,
    undo,
    setRow,
    slotFill: shownNumber ? { kind: "text", value: shownNumber, style: "number" } : null,
  };
}
